/**
 * apiClient — thin fetch wrapper for the chatbot UI.
 *
 * Auth model: HttpOnly Cookie set by the backend after the user signs in
 * via Cognito Hosted UI (see utils/auth.js + backend auth/cognito.py).
 * Every request is sent with `credentials: 'include'` so the browser
 * automatically attaches the cookie. JS in this tab cannot read it, no
 * api_key is baked into the JS bundle, and a security scanner crawling
 * the deployed assets finds nothing sensitive.
 */

const API_ENDPOINT = import.meta.env.VITE_API_ENDPOINT || 'http://localhost:8080';

const resolveUrl = (path) => {
  if (!path) {
    throw new Error('apiFetch requires a path');
  }
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  return `${API_ENDPOINT.replace(/\/$/, '')}${path.startsWith('/') ? path : `/${path}`}`;
};

/**
 * Authenticated fetch — sends the HttpOnly cookie automatically.
 *
 * Retries-on-401 is intentionally NOT done here. If the cookie is
 * missing/expired, the caller (App.jsx bootstrap, or fetch error
 * handler) decides whether to redirect to /auth/login.
 */
export const apiFetch = (path, options = {}) => {
  const { headers, credentials, ...rest } = options;
  return fetch(resolveUrl(path), {
    ...rest,
    headers: new Headers(headers || {}),
    // Always include the cookie. `omit`/`same-origin` would defeat the
    // whole auth model — make it impossible to misconfigure per call.
    credentials: 'include',
  });
};

export const getApiEndpoint = () => API_ENDPOINT;

/**
 * Get WebSocket endpoint for real-time agent communication.
 * Falls back to local development WebSocket server if not configured.
 */
export const getWebSocketEndpoint = () => {
  const wsEndpoint = import.meta.env.VITE_WS_ENDPOINT;
  if (!wsEndpoint) {
    console.warn('[apiClient] VITE_WS_ENDPOINT is not configured. Falling back to local development WebSocket.');
    return 'ws://localhost:8081';
  }
  return wsEndpoint;
};

// =============================================================================
// Compatibility shims for code that still imports the older auth helpers.
// All of these are no-ops in the cookie-auth world; they exist purely to
// avoid touching the many call sites in App.jsx / components/* until they
// can be cleaned up incrementally.
// =============================================================================
export const getApiKey = () => null;
export const getOktaToken = () => null;
export const getAuthToken = () => null;
export const getAuthHeader = () => null;
